import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Loader2, ChevronRight, Search, GitBranch, Layers, Package, FileCheck, FileX, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STATUS_CFG = {
  UNALLOCATED: { color: '#ef4444', label: 'Unalloc' },
  PARTIAL_SOFT: { color: '#eab308', label: 'Soft' },
  READY_SOFT: { color: '#3b82f6', label: 'Rdy Soft' },
  PO_READY: { color: '#143A6B', label: 'PO Rdy' },
  PARTIAL_HARD: { color: '#f59e0b', label: 'Partial' },
  FULLY_ALLOCATED: { color: '#22c55e', label: 'Full' },
};

const FAB_LABELS = { cuttingAndFitting: 'Cut&Fit', welding: 'Weld', surfaceTreatment: 'Surface', dispatch: 'Dispatch' };
const FAB_STAGES = Object.keys(FAB_LABELS);

/* ─── Connector components with fixed pixel offsets ─── */
const AREA_CENTER = 20;
const TAG_CENTER = 18;
const MARK_CENTER = 14;

function VLine({ isFirst, isLast, color = 'var(--border-color)', center = 18 }) {
  return (
    <div className="flex-shrink-0 self-stretch relative" style={{ width: 1 }}>
      <div className="absolute left-0" style={{
        top: isFirst ? center : 0,
        bottom: isLast ? `calc(100% - ${center}px)` : 0,
        width: 1, background: color
      }} />
    </div>
  );
}

function HLine({ color = 'var(--border-color)', center = 18 }) {
  return (
    <div className="w-6 flex-shrink-0 self-stretch relative">
      <div className="absolute left-0 right-0" style={{ top: center, height: 1, background: color }} />
    </div>
  );
}

function ParentConn({ color = 'var(--border-color)', parentCenter = 20 }) {
  return (
    <div className="w-6 flex-shrink-0 self-stretch relative">
      <div className="absolute left-0 right-0" style={{ top: parentCenter, height: 1, background: color }} />
    </div>
  );
}

/* ─── Leaf node: Mark Number ─── */
function MarkNode({ mark }) {
  const allocPct = mark.overallAllocatedPercent || 0;
  const issued = !!mark.materialIssueRequestNo;
  const fabBlocked = mark.fabricationBlocked;
  const fab = mark.stageGates?.fabrication || {};
  const currentStage = FAB_STAGES.find(s => !fab[s]?.isComplete) || 'DONE';
  const fabPct = mark.fabricationProgressPct || 0;

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded border transition-all hover:brightness-95 whitespace-nowrap"
      style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)', borderLeft: `2px solid ${allocPct >= 100 ? '#22c55e' : allocPct > 0 ? '#f59e0b' : '#ef4444'}` }}
      data-testid={`tree-mark-${mark.markNumber}`}>
      <Package className="w-3 h-3 flex-shrink-0" style={{ color: '#F59206' }} />
      <span className="text-[9px] font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{mark.markNumber}</span>
      <span className="text-[8px]" style={{ color: 'var(--text-muted)' }}>{mark.section}</span>
      <span className="text-[7px] font-mono" style={{ color: 'var(--text-muted)' }}>{(mark.totalWeight || 0).toFixed(0)}kg</span>
      {issued ? <FileCheck className="w-2.5 h-2.5" style={{ color: '#22c55e' }} /> : allocPct >= 100 ? <FileX className="w-2.5 h-2.5 opacity-40" style={{ color: '#ef4444' }} /> : null}
      {fabBlocked && <Lock className="w-2.5 h-2.5" style={{ color: '#ef4444' }} />}
      {!fabBlocked && fabPct > 0 && (
        <span className="text-[7px] px-1 py-0.5 rounded-full" style={{ background: '#143A6B20', color: '#143A6B' }}>
          {currentStage === 'DONE' ? 'Done' : FAB_LABELS[currentStage]} {fabPct.toFixed(0)}%
        </span>
      )}
    </div>
  );
}

/* ─── Tag node with expandable marks ─── */
function TagNode({ tag, projectId, isFirst, isLast, isExpanded, onToggle }) {
  const [marks, setMarks] = useState(null);
  const [loading, setLoading] = useState(false);
  const wf = tag.workfrontStatus || 'UNALLOCATED';
  const stCfg = STATUS_CFG[wf] || STATUS_CFG.UNALLOCATED;
  const hardPct = tag.hardAllocation?.allocationPercent || 0;
  const fabPct = tag.fabricationProgress?.overallFabProgressPct || 0;

  useEffect(() => {
    if (isExpanded && !marks) {
      setLoading(true);
      api.get(`/v1/projects/${projectId}/swm/mark-numbers?structuralTag=${tag.structuralTag}`)
        .then(r => setMarks(r.data?.markNumbers || []))
        .catch(() => setMarks([]))
        .finally(() => setLoading(false));
    }
  }, [isExpanded, projectId, tag.structuralTag, marks]);

  return (
    <div className="flex items-stretch" data-testid={`tree-tag-${tag.structuralTag}`}>
      <VLine isFirst={isFirst} isLast={isLast} color={stCfg.color + '60'} center={TAG_CENTER} />
      <HLine color={stCfg.color + '60'} center={TAG_CENTER} />
      <div className="flex flex-col">
        {/* Tag card */}
        <button onClick={onToggle} className="flex items-center gap-1.5 px-2.5 py-1.5 my-0.5 rounded-lg border transition-all hover:brightness-95 whitespace-nowrap flex-shrink-0"
          style={{ background: isExpanded ? `${stCfg.color}08` : 'var(--card-bg)', borderColor: isExpanded ? stCfg.color : 'var(--border-color)', borderLeft: `3px solid ${stCfg.color}` }}>
          <ChevronRight className="w-3 h-3 flex-shrink-0 transition-transform" style={{ color: stCfg.color, transform: isExpanded ? 'rotate(90deg)' : 'none' }} />
          <Layers className="w-3 h-3 flex-shrink-0" style={{ color: '#F59206' }} />
          <span className="text-[9px] font-mono font-bold" style={{ color: 'var(--text-primary)' }}>{tag.structuralTag}</span>
          {tag.priority && <span className="text-[7px] font-bold px-1 rounded" style={{ background: '#F5920620', color: '#F59206' }}>P{tag.priority}</span>}
          <span className="text-[7px] font-mono" style={{ color: 'var(--text-muted)' }}>{(tag.totalMTOWeight || 0).toFixed(0)}kg</span>
          <span className="text-[8px] font-mono" style={{ color: hardPct >= 100 ? '#22c55e' : '#f59e0b' }}>{hardPct.toFixed(0)}%</span>
          {fabPct > 0 && <span className="text-[7px] font-mono" style={{ color: '#143A6B' }}>F:{fabPct.toFixed(0)}%</span>}
          <span className="text-[7px] px-1 py-0.5 rounded-full" style={{ background: stCfg.color + '20', color: stCfg.color }}>{stCfg.label}</span>
        </button>

        {/* Marks */}
        {isExpanded && (
          <div className="flex items-start ml-2">
            <ParentConn color="#F5920640" parentCenter={MARK_CENTER} />
            <div className="flex flex-col">
              {loading ? (
                <div className="flex items-center gap-1 py-2 px-3">
                  <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--text-muted)' }} />
                  <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>Loading...</span>
                </div>
              ) : marks && marks.length > 0 ? (
                marks.map((m, idx) => (
                  <div key={m.markNumber} className="flex items-stretch">
                    <VLine isFirst={idx === 0} isLast={idx === marks.length - 1} color="#F5920640" center={MARK_CENTER} />
                    <HLine color="#F5920640" center={MARK_CENTER} />
                    <div className="my-0.5"><MarkNode mark={m} /></div>
                  </div>
                ))
              ) : (
                <span className="text-[9px] py-1 px-3" style={{ color: 'var(--text-muted)' }}>No marks</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Area node (group of tags) ─── */
function AreaNode({ area, group, projectId, isFirst, isLast, isExpanded, onToggle, expandedTags, toggleTag }) {
  return (
    <div className="flex items-stretch">
      <VLine isFirst={isFirst} isLast={isLast} color="#3b82f640" center={AREA_CENTER} />
      <HLine color="#3b82f640" center={AREA_CENTER} />
      <div className="flex flex-col">
        {/* Area card */}
        <button onClick={onToggle} className="flex items-center gap-1.5 px-3 py-2 my-0.5 rounded-lg border transition-all hover:brightness-95 whitespace-nowrap flex-shrink-0"
          style={{ background: isExpanded ? '#3b82f608' : 'var(--card-bg)', borderColor: isExpanded ? '#3b82f6' : 'var(--border-color)', borderLeft: '3px solid #3b82f6' }}
          data-testid={`tree-area-${area}`}>
          <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 transition-transform" style={{ color: '#3b82f6', transform: isExpanded ? 'rotate(90deg)' : 'none' }} />
          <span className="text-[10px] font-bold" style={{ color: '#3b82f6' }}>{area}</span>
          <span className="text-[8px] px-1.5 py-0.5 rounded" style={{ background: '#3b82f610', color: '#3b82f6' }}>{group.tags.length}</span>
          <span className="text-[8px] font-mono" style={{ color: 'var(--text-muted)' }}>{(group.totalWeight / 1000).toFixed(1)}T</span>
        </button>

        {/* Tags */}
        {isExpanded && (
          <div className="flex items-start ml-2">
            <ParentConn color="#3b82f640" parentCenter={TAG_CENTER} />
            <div className="flex flex-col">
              {group.tags.map((tag, tIdx) => (
                <TagNode key={tag.structuralTag} tag={tag} projectId={projectId}
                  isFirst={tIdx === 0} isLast={tIdx === group.tags.length - 1}
                  isExpanded={expandedTags.has(tag.structuralTag)}
                  onToggle={() => toggleTag(tag.structuralTag)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════ MAIN COMPONENT ═══════════════════ */
export default function SWMDecompositionTree({ projectId, filters }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedAreas, setExpandedAreas] = useState(new Set());
  const [expandedTags, setExpandedTags] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters?.workfrontStatus) params.set('workfrontStatus', filters.workfrontStatus);
      if (statusFilter) params.set('workfrontStatus', statusFilter);
      const res = await api.get(`/v1/projects/${projectId}/swm/tags?${params}`);
      setTags(res.data?.tags || []);
    } catch { setTags([]); }
    setLoading(false);
  }, [projectId, filters, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredTags = useMemo(() => {
    return tags.filter(t => {
      if (search && !t.structuralTag?.toLowerCase().includes(search.toLowerCase()) && !t.structureNameRef?.toLowerCase().includes(search.toLowerCase())) return false;
      if (filters?.area && t.area !== filters.area) return false;
      if (filters?.priority && t.priority !== Number(filters.priority)) return false;
      return true;
    });
  }, [tags, search, filters]);

  const areaGroups = useMemo(() => {
    const groups = {};
    filteredTags.forEach(t => {
      const area = t.area || 'Unassigned';
      if (!groups[area]) groups[area] = { tags: [], totalWeight: 0 };
      groups[area].tags.push(t);
      groups[area].totalWeight += t.totalMTOWeight || 0;
    });
    return groups;
  }, [filteredTags]);

  const areas = Object.keys(areaGroups).sort();
  const toggleArea = (area) => setExpandedAreas(prev => { const n = new Set(prev); n.has(area) ? n.delete(area) : n.add(area); return n; });
  const toggleTag = (tag) => setExpandedTags(prev => { const n = new Set(prev); n.has(tag) ? n.delete(tag) : n.add(tag); return n; });
  const expandAll = () => setExpandedAreas(new Set(areas));
  const collapseAll = () => { setExpandedAreas(new Set()); setExpandedTags(new Set()); };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  return (
    <div className="space-y-3" data-testid="swm-decomposition-tree">
      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border flex-1 max-w-xs" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)' }}>
          <Search className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tag / structure..."
            className="bg-transparent border-none outline-none text-[11px] w-full" style={{ color: 'var(--text-primary)' }} data-testid="tree-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-[10px] px-2 py-1.5 rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} data-testid="tree-status-filter">
          <option value="">All Status</option>
          {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <Button size="sm" variant="outline" onClick={expandAll} className="text-[9px] h-6" data-testid="tree-expand-all">Expand All</Button>
        <Button size="sm" variant="outline" onClick={collapseAll} className="text-[9px] h-6" data-testid="tree-collapse-all">Collapse All</Button>
        <div className="ml-auto text-[9px] flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
          <GitBranch className="w-3 h-3" /> {areas.length} areas, {filteredTags.length} tags
        </div>
      </div>

      {/* Horizontal Tree */}
      <div className="rounded-lg border overflow-x-auto" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }} data-testid="tree-root">
        <div className="p-4 flex items-start min-w-max">
          {/* Root node */}
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg flex-shrink-0"
            style={{ background: '#F5920610', border: '1px solid #F5920630' }}>
            <GitBranch className="w-4 h-4" style={{ color: '#F59206' }} />
            <div>
              <div className="text-[11px] font-bold" style={{ color: '#F59206' }}>Structural Workfront</div>
              <div className="text-[8px] font-mono" style={{ color: 'var(--text-muted)' }}>{filteredTags.length} tags | {areas.length} areas</div>
            </div>
          </div>

          {areas.length > 0 && (
            <div className="flex items-start">
              <ParentConn color="#F5920640" parentCenter={AREA_CENTER} />
              <div className="flex flex-col">
                {areas.map((area, aIdx) => (
                  <AreaNode key={area} area={area} group={areaGroups[area]} projectId={projectId}
                    isFirst={aIdx === 0} isLast={aIdx === areas.length - 1}
                    isExpanded={expandedAreas.has(area)} onToggle={() => toggleArea(area)}
                    expandedTags={expandedTags} toggleTag={toggleTag} />
                ))}
              </div>
            </div>
          )}

          {areas.length === 0 && (
            <div className="flex items-center ml-4">
              <span className="text-sm" style={{ color: 'var(--text-muted)' }}>No data. Seed from Data Import tab.</span>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-[9px] px-1 flex-wrap" style={{ color: 'var(--text-muted)' }}>
        {Object.entries(STATUS_CFG).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-sm" style={{ background: v.color }} />
            <span>{v.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1"><FileCheck className="w-2.5 h-2.5" style={{ color: '#22c55e' }} /><span>Issued</span></div>
        <div className="flex items-center gap-1"><Lock className="w-2.5 h-2.5" style={{ color: '#ef4444' }} /><span>Fab Blocked</span></div>
      </div>
    </div>
  );
}
